// pageEditorLayoutUtils.js

// Utilities
import { generateKey } from 'utilities/Keys';
import { getViewRowTitle } from 'utilities/ViewRows';

function normalizeString(value) {
    return String(value || '').trim();
}

export function buildLayoutSlot() {
    const key = generateKey();

    return {
        blockKey: null,
        key,
        kind: 'block_slot',
    };
}

export function buildLayoutContainer(kind, childKeys = []) {
    return {
        children: Array.isArray(childKeys) ? childKeys : [],
        key: generateKey(),
        kind,
    };
}

export function buildLayoutRow(slotCount = 2) {
    const slotNodes = Array.from({ length: slotCount }).map(() => buildLayoutSlot());

    return {
        rowNode: buildLayoutContainer('row', slotNodes.map((slot) => slot.key)),
        slotNodes,
    };
}

export function buildLayoutStack(slotCount = 2) {
    const slotNodes = Array.from({ length: slotCount }).map(() => buildLayoutSlot());

    return {
        slotNodes,
        stackNode: buildLayoutContainer('stack', slotNodes.map((slot) => slot.key)),
    };
}

export function isContainerKind(kind) {
    return kind === 'row' || kind === 'stack';
}

export function canContainerAcceptChildKind(parentKind, childKind) {
    if (parentKind === 'stack') {
        return childKind === 'block_slot' || childKind === 'row';
    }

    if (parentKind === 'row') {
        return childKind === 'block_slot' || childKind === 'stack';
    }

    return false;
}

export function getContainerOrientation(kind) {
    return kind === 'row' ? 'horizontal' : 'vertical';
}

export function getContainerLabel(kind) {
    return kind === 'stack' ? 'STACK' : 'ROW';
}

export function moveListItem(list = [], fromIndex, toIndex) {
    const next = [...list];
    const [item] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, item);
    return next;
}

export function cloneBlocksByKey(blocksByKey) {
    return blocksByKey && typeof blocksByKey === 'object'
        ? Object.entries(blocksByKey).reduce((acc, [key, value]) => {
            acc[key] = { ...(value || {}) };
            return acc;
        }, {})
        : {};
}

export function getNodeBlockKey(node) {
    const blockKey = String(node?.blockKey || '').trim();
    return blockKey || '';
}

export function getEmbeddedViewDefinition(block) {
    return block?.view && typeof block.view === 'object'
        ? block.view
        : null;
}

export function getViewBlockDisplayTitle(block, view = null, { fallback = '' } = {}) {
    const titleOverride = normalizeString(block?.titleOverride);
    if (titleOverride) {
        return titleOverride;
    }

    const resolvedView = view || getEmbeddedViewDefinition(block);
    const viewTitle = normalizeString(resolvedView?.title);
    if (viewTitle) {
        return viewTitle;
    }

    const fallbackTitle = normalizeString(fallback);
    if (fallbackTitle) {
        return fallbackTitle;
    }

    return normalizeString(getViewRowTitle(resolvedView));
}

export function collectBlockKeysForNode(layout, nodeKey) {
    const node = layout?.nodes?.[nodeKey];
    if (!node) return [];

    const blockKeys = [];
    const ownBlockKey = getNodeBlockKey(node);
    if (ownBlockKey) {
        blockKeys.push(ownBlockKey);
    }

    if (Array.isArray(node.children)) {
        node.children.forEach((childKey) => {
            blockKeys.push(...collectBlockKeysForNode(layout, childKey));
        });
    }

    return Array.from(new Set(blockKeys));
}

export function removeBlockKeys(blocksByKey, blockKeys = []) {
    const nextBlocksByKey = cloneBlocksByKey(blocksByKey);
    blockKeys.forEach((blockKey) => {
        delete nextBlocksByKey[blockKey];
    });
    return nextBlocksByKey;
}

export function isLayoutNodeDescendantOf(layout, nodeKey, ancestorKey) {
    const cleanedNodeKey = normalizeString(nodeKey);
    const cleanedAncestorKey = normalizeString(ancestorKey);

    if (!cleanedNodeKey || !cleanedAncestorKey) {
        return false;
    }

    let currentKey = cleanedNodeKey;

    while (currentKey) {
        if (currentKey === cleanedAncestorKey) {
            return true;
        }

        currentKey = findParentNodeKey(layout, currentKey);
    }

    return false;
}

export function isLayoutEditorTextInteractionTarget(target) {
    if (!target || typeof target.closest !== 'function') {
        return false;
    }

    return Boolean(target.closest([
        '.rich-text-editor-container',
        '.layout-editor-slot-content-rich-text-menu-bar',
    ].join(', ')));
}

export function findParentNodeKey(layout, nodeKey) {
    if (!nodeKey) return '';

    const parentNode = Object.values(layout?.nodes || {}).find((node) => (
        Array.isArray(node?.children) && node.children.includes(nodeKey)
    ));

    return String(parentNode?.key || '').trim();
}

export function canMoveNodeToParent(node, targetParent) {
    if (!node || !targetParent || !Array.isArray(targetParent?.children)) return false;

    return canContainerAcceptChildKind(targetParent.kind, node.kind);
}

function buildWrappedRootRowKey(nodes, slotKey) {
    const baseKey = `${slotKey}__row`;
    if (!nodes[baseKey]) return baseKey;

    let suffix = 1;
    while (nodes[`${baseKey}_${suffix}`]) {
        suffix += 1;
    }

    return `${baseKey}_${suffix}`;
}

export function cloneLayout(layout) {
    const nodes = layout?.nodes && typeof layout.nodes === 'object'
        ? Object.entries(layout.nodes).reduce((acc, [key, value]) => {
            acc[key] = Array.isArray(value?.children)
                ? { ...value, children: [...value.children] }
                : { ...(value || {}) };
            return acc;
        }, {})
        : {};

    const rootNodeKey = String(layout?.rootNodeKey || 'layout_root').trim() || 'layout_root';
    if (!nodes[rootNodeKey]) {
        nodes[rootNodeKey] = {
            key: rootNodeKey,
            kind: 'stack',
            children: [],
        };
    }

    const rootChildren = Array.isArray(nodes[rootNodeKey]?.children)
        ? nodes[rootNodeKey].children
        : [];

    nodes[rootNodeKey].children = rootChildren.map((childKey) => {
        const childNode = nodes[childKey];
        if (!childNode || childNode.kind !== 'block_slot') return childKey;

        const rowKey = buildWrappedRootRowKey(nodes, childKey);
        nodes[rowKey] = {
            key: rowKey,
            kind: 'row',
            children: [childKey],
        };

        return rowKey;
    });

    return {
        rootNodeKey,
        nodes,
    };
}

export function removeNodeDeep(layout, nodeKey) {
    const nextLayout = cloneLayout(layout);
    const keysToDelete = new Set();

    function visit(targetKey) {
        const targetNode = nextLayout.nodes[targetKey];
        if (!targetNode || keysToDelete.has(targetKey)) return;

        keysToDelete.add(targetKey);

        if (Array.isArray(targetNode.children)) {
            targetNode.children.forEach((childKey) => {
                visit(childKey);
            });
        }
    }

    visit(nodeKey);
    if (!keysToDelete.size) return nextLayout;

    Object.values(nextLayout.nodes).forEach((node) => {
        if (!Array.isArray(node?.children)) return;
        node.children = node.children.filter((childKey) => !keysToDelete.has(childKey));
    });

    keysToDelete.forEach((targetKey) => {
        delete nextLayout.nodes[targetKey];
    });

    return nextLayout;
}

export function pruneEmptyAncestorContainers(layout, startNodeKey) {
    const nextLayout = cloneLayout(layout);
    let currentNodeKey = String(startNodeKey || '').trim();

    while (currentNodeKey && currentNodeKey !== nextLayout.rootNodeKey) {
        const currentNode = nextLayout.nodes[currentNodeKey];
        if (!currentNode || !isContainerKind(currentNode.kind)) break;
        if (Array.isArray(currentNode.children) && currentNode.children.length > 0) break;

        const parentNodeKey = findParentNodeKey(nextLayout, currentNodeKey);
        if (!parentNodeKey) break;

        const parentNode = nextLayout.nodes[parentNodeKey];
        const emptyNodeKey = currentNodeKey;
        if (Array.isArray(parentNode?.children)) {
            parentNode.children = parentNode.children.filter((childKey) => childKey !== emptyNodeKey);
        }

        delete nextLayout.nodes[emptyNodeKey];
        currentNodeKey = parentNodeKey;
    }

    return nextLayout;
}

export function moveNodeToTarget(layout, nodeKey, targetParentKey, targetInsertIndex) {
    const nextLayout = cloneLayout(layout);
    const node = nextLayout.nodes[nodeKey];
    const sourceParentKey = findParentNodeKey(nextLayout, nodeKey);
    const sourceParent = nextLayout.nodes[sourceParentKey];
    const targetParent = nextLayout.nodes[targetParentKey];

    if (!node || !sourceParent || !targetParent) return null;
    if (!canMoveNodeToParent(node, targetParent)) return null;

    const sourceChildren = [...(sourceParent.children || [])];
    const sourceIndex = sourceChildren.indexOf(nodeKey);
    if (sourceIndex < 0) return null;

    sourceChildren.splice(sourceIndex, 1);
    sourceParent.children = sourceChildren;

    const targetChildren = sourceParentKey === targetParentKey
        ? [...sourceChildren]
        : [...(targetParent.children || [])];

    let safeInsertIndex = Math.max(0, Math.min(targetInsertIndex, targetChildren.length));

    if (sourceParentKey === targetParentKey && sourceIndex < safeInsertIndex) {
        safeInsertIndex -= 1;
    }

    if (sourceParentKey === targetParentKey && safeInsertIndex === sourceIndex) {
        return null;
    }

    targetChildren.splice(safeInsertIndex, 0, nodeKey);
    targetParent.children = targetChildren;

    if (sourceParentKey !== targetParentKey && sourceParent.children.length === 0) {
        return pruneEmptyAncestorContainers(nextLayout, sourceParentKey);
    }

    return nextLayout;
}

export function swapNodeWithEmptySlot(layout, nodeKey, emptySlotKey) {
    const nextLayout = cloneLayout(layout);
    const node = nextLayout.nodes[nodeKey];
    const emptySlotNode = nextLayout.nodes[emptySlotKey];
    const sourceParentKey = findParentNodeKey(nextLayout, nodeKey);
    const targetParentKey = findParentNodeKey(nextLayout, emptySlotKey);
    const sourceParent = nextLayout.nodes[sourceParentKey];
    const targetParent = nextLayout.nodes[targetParentKey];

    if (!node || !emptySlotNode || nodeKey === emptySlotKey) return null;
    if (emptySlotNode.kind !== 'block_slot' || getNodeBlockKey(emptySlotNode)) return null;
    if (!sourceParent || !targetParent) return null;
    if (!canMoveNodeToParent(node, targetParent)) return null;
    if (!canMoveNodeToParent(emptySlotNode, sourceParent)) return null;

    const sourceIndex = Array.isArray(sourceParent.children) ? sourceParent.children.indexOf(nodeKey) : -1;
    const targetIndex = Array.isArray(targetParent.children) ? targetParent.children.indexOf(emptySlotKey) : -1;
    if (sourceIndex < 0 || targetIndex < 0) return null;

    if (sourceParentKey === targetParentKey) {
        const nextChildren = [...sourceParent.children];
        nextChildren[sourceIndex] = emptySlotKey;
        nextChildren[targetIndex] = nodeKey;
        sourceParent.children = nextChildren;
        return nextLayout;
    }

    const nextSourceChildren = [...sourceParent.children];
    const nextTargetChildren = [...targetParent.children];
    nextSourceChildren[sourceIndex] = emptySlotKey;
    nextTargetChildren[targetIndex] = nodeKey;
    sourceParent.children = nextSourceChildren;
    targetParent.children = nextTargetChildren;

    return nextLayout;
}